/**
 * @author: lv.chen3
 * @desc: LocationSelect组件
 * @date: 2025/8/10
 */
import React, { useEffect, useMemo, useState } from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { Dropdown, List } from 'tea-component';
import { getOptionText } from '@intelligence/utils/topchartsUtils';
import { ICON_ARROW_RIGHT } from '@intelligence/v3/constants/icon';

import type { ILocationOption } from './types';

import './index.scss';

interface ILocationSelectProps {
  options: ILocationOption[];
  value: string[];
  onChange: (value: string[], context: { options: ILocationOption[] }) => void;
}

const LocationSelect: React.FC<ILocationSelectProps> = (props) => {
  const { options, value, onChange } = props;

  const { locale, formatMessage } = useIntl();
  const [activeL1Option, setActiveL1Option] = useState<ILocationOption | null>(null);
  const [activeL2Option, setActiveL2Option] = useState<ILocationOption | null>(null);

  const hasLevel3 = !!activeL2Option?.children?.length;
  const overlayClassName = useMemo(
    () => `dbu-report-location-select-dropdown-overlay${hasLevel3 ? ' has-l3' : ''}`,
    [hasLevel3],
  );

  const getDisplayText = (option?: ILocationOption | null): string => {
    if (!option) return '';
    const { value, text, textCN, children } = option;
    const normalized = {
      value,
      text: text || '',
      textCN: textCN || '',
      textEN: text || '',
      children,
    };
    return getOptionText(locale, normalized as any);
  };

  const renderButton = () => {
    const [l1Value, l2Value, l3Value] = value || [];
    if (!Array.isArray(options) || options.length === 0 || !l1Value || !l2Value)
      return (
        <div className="dbu-report-location-select-button">
          <span className="dbu-report-location-select-placeholder">
            {formatMessage({ id: 'dbu_report_location_placeholder' })}
          </span>
        </div>
      );

    const l1Option = options.find((option) => option?.value === l1Value);
    const l2Option = l1Option?.children?.find((option) => option?.value === l2Value);
    const l3Option = l2Option?.children?.find((option) => option?.value === l3Value);

    const l1Text = getDisplayText(l1Option);
    const l2Text = getDisplayText(l2Option);

    let displayText = `${l1Text}: ${l2Text}`;
    if (l3Value && l3Option) {
      const l3Text = getDisplayText(l3Option);
      displayText += ` - ${l3Text}`;
    }

    return (
      <div className="dbu-report-location-select-button">
        <span className="dbu-report-location-select-value">{displayText}</span>
      </div>
    );
  };

  useEffect(() => {
    if (!Array.isArray(options) || options.length === 0) {
      setActiveL1Option(null);
      setActiveL2Option(null);
      return;
    }

    const [l1Value, l2Value] = value || [];
    const l1Option = options.find((option) => option?.value === l1Value) || options[0];
    const l2Option = l1Option?.children?.find((option) => option?.value === l2Value);

    setActiveL1Option(l1Option || null);
    setActiveL2Option(l2Option?.children?.length ? l2Option : null);
  }, [options, value]);

  return (
    <Dropdown
      className="dbu-report-location-select-dropdown"
      overlayClassName={overlayClassName}
      appearance="button"
      placementOffset={10}
      button={renderButton()}
    >
      <div className="dbu-report-location-select-dropdown-body">
        <div className="dbu-report-location-select-panel">
          <div className="dbu-report-location-select-panel-title">
            {formatMessage({ id: 'dbu_report_location_level1' })}
          </div>
          <div className="dbu-report-location-select-panel-list-wrapper">
            <List className="dbu-report-location-select-panel-list" type="option">
              {options.map((option) => {
                const optionValue = option?.value || '';
                const optionText = getDisplayText(option);
                const isDisabled = option?.disabled === true;

                return (
                  <List.Item
                    key={optionValue}
                    className={classnames('dbu-report-location-select-panel-item', {
                      'is-disabled': isDisabled,
                    })}
                    selected={optionValue === activeL1Option?.value}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isDisabled) {
                        return;
                      }

                      setActiveL1Option(option);
                      setActiveL2Option(null);
                    }}
                  >
                    <span className="dbu-report-location-select-panel-item-text" title={optionText}>
                      {optionText}
                    </span>
                    <img className="dbu-report-location-select-panel-item-icon-arrow" src={ICON_ARROW_RIGHT} alt="" />
                  </List.Item>
                );
              })}
            </List>
          </div>
        </div>
        <div className="dbu-report-location-select-panel-splitter"></div>
        {!!activeL1Option && (
          <div className="dbu-report-location-select-panel">
            <div className="dbu-report-location-select-panel-title">
              {formatMessage({ id: 'dbu_report_location_level2' })}
            </div>
            <div className="dbu-report-location-select-panel-list-wrapper">
              <List className="dbu-report-location-select-panel-list" type="option">
                {activeL1Option.children?.map((option: ILocationOption) => {
                  const optionValue = option?.value || '';
                  const optionText = getDisplayText(option);
                  const hasChildren = option.children && option.children.length > 0;
                  return (
                    <List.Item
                      key={optionValue}
                      className="dbu-report-location-select-panel-item"
                      selected={optionValue === (activeL2Option?.value || value?.[1])}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (hasChildren) {
                          setActiveL2Option(option);
                        } else {
                          if (!activeL1Option) return;
                          setActiveL2Option(null);
                          onChange([activeL1Option.value, optionValue], { options: [activeL1Option, option] });
                        }
                      }}
                    >
                      <span className="dbu-report-location-select-panel-item-text" title={optionText}>
                        {optionText}
                      </span>
                      {hasChildren && (
                        <img
                          className="dbu-report-location-select-panel-item-icon-arrow"
                          src={ICON_ARROW_RIGHT}
                          alt=""
                        />
                      )}
                    </List.Item>
                  );
                })}
              </List>
            </div>
          </div>
        )}

        {!!activeL2Option && (
          <>
            <div className="dbu-report-location-select-panel-splitter"></div>
            <div className="dbu-report-location-select-panel">
              <div className="dbu-report-location-select-panel-title">
                {formatMessage({ id: 'dbu_report_location_level3' })}
              </div>
              <div className="dbu-report-location-select-panel-list-wrapper">
                <List className="dbu-report-location-select-panel-list" type="option">
                  {activeL2Option.children?.map((option: ILocationOption) => {
                    const optionValue = option?.value || '';
                    const optionText = getDisplayText(option);
                    return (
                      <List.Item
                        key={optionValue}
                        className="dbu-report-location-select-panel-item"
                        selected={optionValue === value?.[2]}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!activeL1Option) return;
                          onChange([activeL1Option.value, activeL2Option.value, optionValue], {
                            options: [activeL1Option, activeL2Option, option],
                          });
                        }}
                      >
                        <span className="dbu-report-location-select-panel-item-text" title={optionText}>
                          {optionText}
                        </span>
                      </List.Item>
                    );
                  })}
                </List>
              </div>
            </div>
          </>
        )}
      </div>
    </Dropdown>
  );
};

export default LocationSelect;
